import { MerchantCreateComponent } from '../pages/merchant-create/merchant-create.component';
import { ProvidersPageComponent } from '../pages/providers/providers-page.component';
import { MerchantAppDetailComponent } from '../pages/merchant-app-detail/merchant-app-detail.component';
import { MerchantDetailComponent } from '../pages/merchant-detail/merchant-detail.component';
import { JournalListComponent } from '../pages/journal-list/journal-list.component';
import { JournalDetailComponent } from '../pages/journal-detail/journal-detail.component';
import { JournalCreateComponent } from '../pages/journal-create/journal-create.component';
import { AccountDetailComponent } from '../pages/account-detail/account-detail.component';
import { WalletDetailComponent } from '../pages/wallet-detail/wallet-detail.component';
import { WebhookDetailComponent } from '../pages/webhook-detail/webhook-detail.component';
import { MessageQueuesDashboardComponent } from '../pages/message-queues-dashboard/message-queues-dashboard.component';
import { CampaignCreateComponent } from '../pages/campaign-create/campaign-create.component';
import { MailingsPageComponent } from '../pages/mailings/mailings-page.component';
import { CampaignDetailComponent } from '../pages/campaign-detail/campaign-detail.component';
import { MessageQueueDetailComponent } from '../pages/message-queue-detail/message-queue-detail.component';
import { BatchDisbursementListComponent } from '../pages/batch-disbursement-list/batch-disbursement-list.component';
import { BatchDisbursementCreateComponent } from '../pages/batch-disbursement-create/batch-disbursement-create.component';
import { ProviderBankAccountDetailComponent } from '../pages/provider-bank-account-detail/provider-bank-account-detail.component';
import { TransactionHubComponent } from '../pages/transaction-hub/transaction-hub.component';
import { SettlementDetailComponent } from '../pages/settlement-detail/settlement-detail.component';
import { BatchDisbursementDetailComponent } from '../pages/batch-disbursement-detail/batch-disbursement-detail.component';
import { FeesHubPageComponent } from '../pages/fees-hub/fees-hub-page.component';
import { ProviderBalancesPageComponent } from '../pages/provider-balances/provider-balances-page.component';
import { FinancialEntityBalancesPageComponent } from '../pages/financial-entity-balances/financial-entity-balances-page.component';
import {
  RoleCode,
  canAccessWithAllowedRoles,
  type RoleCodeValue,
} from '../utils/role-access';
import { Type } from '@angular/core';

/**
 * Configuration for a management entity: list URL and optional custom components.
 * Routes use this to render list/detail/create/edit without per-entity components.
 */
export interface EntityConfig {
  /** Route key (e.g. "users", "wallets"). Used in /manage/:entityKey */
  key: string;
  /** Menu label */
  label: string;
  /** API list path (e.g. "auth/users/", "finances/wallets/"). No leading slash. */
  listPath: string;
  /** When false, hide the list page "Add new" button (default true). */
  showCreateButton?: boolean;
  /** When false, hide the detail page "Edit" link (default true). */
  showEditButton?: boolean;
  /** Optional custom list component (can extend base list and add sections above/below/sides). */
  listComponent?: Type<unknown>;
  /** Optional custom detail view component. */
  detailComponent?: Type<unknown>;
  /** Optional custom create form component. */
  createComponent?: Type<unknown>;
  /** Optional custom edit form component. */
  editComponent?: Type<unknown>;
  /** Role codes that may open this entity in the admin UI; omit or empty = all roles. */
  allowedRoles?: readonly RoleCodeValue[];
}

/**
 * A grouped menu item for the sidebar. Groups contain entities.
 */
export interface MenuGroup {
  /** Unique group key */
  key: string;
  /** Display label for the group */
  label: string;
  /** Lucide icon name for the group (from @ng-icons/lucide) */
  icon: string;
  /** Entity configs within this group */
  items: EntityConfig[];
}

/**
 * Registry of all management entities. Menu and routes are derived from this.
 */
const SYS: RoleCodeValue[] = [RoleCode.SYSTEM_ADMIN, RoleCode.SYSTEM_FINANCIAL, RoleCode.SYSTEM_DEVELOPER];
const SYS_FIN: RoleCodeValue[] = [RoleCode.SYSTEM_ADMIN, RoleCode.SYSTEM_FINANCIAL];
const SYS_ADM_DEV: RoleCodeValue[] = [RoleCode.SYSTEM_ADMIN, RoleCode.SYSTEM_DEVELOPER];

export const entityConfigList: EntityConfig[] = [
  { key: 'users', label: 'Users', listPath: 'auth/users/', allowedRoles: [RoleCode.SYSTEM_ADMIN] },
  { key: 'roles', label: 'Roles', listPath: 'auth/roles/', allowedRoles: [RoleCode.SYSTEM_ADMIN] },
  {
    key: 'merchants',
    label: 'Merchants',
    listPath: 'auth/merchants/',
    detailComponent: MerchantDetailComponent,
    createComponent: MerchantCreateComponent,
    allowedRoles: [RoleCode.SYSTEM_ADMIN],
  },
  {
    key: 'merchant-applications',
    label: 'Applications',
    listPath: 'auth/merchant-applications/',
    detailComponent: MerchantAppDetailComponent,
    allowedRoles: [RoleCode.SYSTEM_ADMIN],
  },
  {
    key: 'api-keys',
    label: 'API Keys',
    listPath: 'auth/api-keys/',
    allowedRoles: [RoleCode.SYSTEM_ADMIN],
  },
  {
    key: 'accounts',
    label: 'Accounts',
    listPath: 'finances/accounts/',
    detailComponent: AccountDetailComponent,
    allowedRoles: [...SYS_FIN],
  },
  {
    key: 'journal-entries',
    label: 'Journal',
    listPath: 'finances/journal-entries/',
    listComponent: JournalListComponent,
    detailComponent: JournalDetailComponent,
    createComponent: JournalCreateComponent,
    allowedRoles: [...SYS_FIN],
  },
  {
    key: 'entities',
    label: 'Entities',
    listPath: 'finances/entities/',
    allowedRoles: SYS_ADM_DEV,
  },
  {
    key: 'wallets',
    label: 'Wallets',
    listPath: 'finances/wallets/',
    detailComponent: WalletDetailComponent,
    allowedRoles: [...SYS],
  },
  {
    key: 'wallet-statements',
    label: 'Wallet Statements',
    listPath: 'finances/wallet-statements/',
    allowedRoles: [...SYS],
  },
  { key: 'currencies', label: 'Currencies', listPath: 'finances/currencies/', allowedRoles: SYS },
  { key: 'countries', label: 'Countries', listPath: 'finances/countries/', allowedRoles: SYS },
  { key: 'exchange-rates', label: 'Exchange Rates', listPath: 'finances/exchange-rates/', allowedRoles: SYS },
  {
    key: 'fee-analytics',
    label: 'Fees Hub',
    listPath: 'finances/fee-analytics/',
    listComponent: FeesHubPageComponent,
    showCreateButton: false,
    showEditButton: false,
    allowedRoles: [...SYS_FIN],
  },
  {
    key: 'provider-balances',
    label: 'Provider Balances',
    listPath: 'finances/provider-balances/',
    listComponent: ProviderBalancesPageComponent,
    showCreateButton: false,
    showEditButton: false,
    allowedRoles: [...SYS_FIN],
  },
  {
    key: 'financial-entity-balances',
    label: 'Entity Balances',
    listPath: 'finances/financial-entity-balances/',
    listComponent: FinancialEntityBalancesPageComponent,
    showCreateButton: false,
    showEditButton: false,
    allowedRoles: [...SYS_FIN],
  },
  {
    key: 'market-currencies',
    label: 'Market Currencies',
    listPath: 'finances/market-currencies/',
    showCreateButton: false,
    allowedRoles: [...SYS_FIN],
  },
  {
    key: 'merchant-funding-topups',
    label: 'Merchant Funding',
    listPath: 'finances/merchant-funding-topups/',
    showCreateButton: false,
    showEditButton: false,
    allowedRoles: [...SYS_FIN],
  },
  {
    key: 'financial-entity-market-currencies',
    label: 'Entity Markets',
    listPath: 'finances/financial-entity-market-currencies/',
    showCreateButton: false,
    allowedRoles: [...SYS_FIN],
  },
  {
    key: 'financial-entity-provider-links',
    label: 'Provider Links',
    listPath: 'finances/financial-entity-provider-links/',
    showCreateButton: false,
    allowedRoles: [...SYS_FIN],
  },
  {
    key: 'pricing-rules',
    label: 'Pricing Rules',
    listPath: 'finances/pricing-rules/',
    allowedRoles: [...SYS_FIN],
  },
  {
    key: 'transaction-amount-limits',
    label: 'Amount limits',
    listPath: 'finances/transaction-amount-limits/',
    allowedRoles: [...SYS_FIN],
  },
  {
    key: 'providers',
    label: 'Providers',
    listPath: 'manage/api-providers/',
    listComponent: ProvidersPageComponent,
    allowedRoles: SYS_ADM_DEV,
  },
  {
    key: 'provider-bank-accounts',
    label: 'Bank accounts',
    listPath: 'records/provider-bank-accounts/',
    showCreateButton: false,
    showEditButton: false,
    detailComponent: ProviderBankAccountDetailComponent,
    allowedRoles: [...SYS_FIN],
  },
  {
    key: 'bank-statements',
    label: 'Bank statements',
    listPath: 'records/bank-statements/',
    showCreateButton: false,
    showEditButton: false,
    detailComponent: TransactionHubComponent,
    allowedRoles: [...SYS_FIN],
  },
  {
    key: 'account-transfers',
    label: 'Account transfers',
    listPath: 'records/account-transfers/',
    showCreateButton: false,
    showEditButton: false,
    detailComponent: TransactionHubComponent,
    allowedRoles: [...SYS_FIN],
  },
  {
    key: 'bank-pulls',
    label: 'Bank pulls',
    listPath: 'records/bank-account-pulls/',
    showCreateButton: false,
    showEditButton: false,
    detailComponent: TransactionHubComponent,
    allowedRoles: [...SYS_FIN],
  },
  {
    key: 'bank-pushes',
    label: 'Bank pushes',
    listPath: 'records/bank-account-pushes/',
    showCreateButton: false,
    showEditButton: false,
    detailComponent: TransactionHubComponent,
    allowedRoles: [...SYS_FIN],
  },
  {
    key: 'deposits',
    label: 'Deposits',
    listPath: 'records/deposits/',
    showCreateButton: false,
    showEditButton: false,
    detailComponent: TransactionHubComponent,
    allowedRoles: [...SYS],
  },
  {
    key: 'payouts',
    label: 'Payouts',
    listPath: 'records/payouts/',
    showCreateButton: false,
    showEditButton: false,
    detailComponent: TransactionHubComponent,
    allowedRoles: [...SYS],
  },
  {
    key: 'refunds',
    label: 'Refunds',
    listPath: 'records/refunds/',
    showCreateButton: false,
    showEditButton: false,
    detailComponent: TransactionHubComponent,
    allowedRoles: [...SYS],
  },
  {
    key: 'remittances',
    label: 'Remittances',
    listPath: 'records/remittances/',
    showCreateButton: false,
    showEditButton: false,
    detailComponent: TransactionHubComponent,
    allowedRoles: [...SYS],
  },
  {
    key: 'settlements',
    label: 'Settlements',
    listPath: 'records/settlements/',
    showCreateButton: false,
    showEditButton: false,
    detailComponent: SettlementDetailComponent,
    allowedRoles: [...SYS],
  },
  {
    key: 'batch-disbursements',
    label: 'Batch Disbursements',
    listPath: 'records/batch-disbursements/',
    showCreateButton: false,
    showEditButton: false,
    listComponent: BatchDisbursementListComponent,
    detailComponent: BatchDisbursementDetailComponent,
    createComponent: BatchDisbursementCreateComponent,
    allowedRoles: [...SYS],
  },
  {
    key: 'notifications',
    label: 'Notifications',
    listPath: 'auth/admin-notifications/',
    allowedRoles: SYS,
  },
  {
    key: 'audit-logs',
    label: 'Audit Logs',
    listPath: 'records/audit-logs/',
    showCreateButton: false,
    showEditButton: false,
    allowedRoles: [RoleCode.SYSTEM_ADMIN, RoleCode.SYSTEM_FINANCIAL],
  },
  {
    key: 'provider-api-logs',
    label: 'Provider API Logs',
    listPath: 'records/provider-api-logs/',
    showCreateButton: false,
    showEditButton: false,
    allowedRoles: SYS,
  },
  {
    key: 'merchant-api-call-logs',
    label: 'Merchant API Call Logs',
    listPath: 'records/merchant-api-call-logs/',
    showCreateButton: false,
    showEditButton: false,
    allowedRoles: [...SYS],
  },
  {
    key: 'webhook-events',
    label: 'Webhook Events',
    listPath: 'manage/webhook-events/',
    detailComponent: WebhookDetailComponent,
    showCreateButton: false,
    showEditButton: false,
    allowedRoles: SYS_ADM_DEV,
  },
  {
    key: 'webhook-rules',
    label: 'Webhook Rules',
    listPath: 'manage/webhook-rules/',
    showCreateButton: false,
    allowedRoles: SYS_ADM_DEV,
  },
  {
    key: 'direct-debits',
    label: 'Direct Debits',
    listPath: 'records/direct-debits/',
    showCreateButton: false,
    showEditButton: false,
    detailComponent: TransactionHubComponent,
    allowedRoles: [...SYS],
  },
  {
    key: 'campaigns',
    label: 'Campaigns',
    listPath: 'auth/campaigns/',
    showEditButton: false,
    detailComponent: CampaignDetailComponent,
    createComponent: CampaignCreateComponent,
    allowedRoles: SYS,
  },
  {
    key: 'campaign-templates',
    label: 'Campaign Templates',
    listPath: 'auth/campaign-templates/',
    allowedRoles: SYS,
  },
  {
    key: 'campaign-categories',
    label: 'Campaign Categories',
    listPath: 'auth/campaign-categories/',
    allowedRoles: SYS,
  },
  {
    key: 'mailings',
    label: 'Mailings',
    listPath: 'auth/campaign-schedules/',
    listComponent: MailingsPageComponent,
    showCreateButton: false,
    showEditButton: false,
    allowedRoles: SYS,
  },
  {
    key: 'message-queues',
    label: 'Message Queues',
    listPath: 'auth/message-queues/',
    detailComponent: MessageQueueDetailComponent,
    listComponent: MessageQueuesDashboardComponent,
    showCreateButton: false,
    showEditButton: false,
    allowedRoles: SYS,
  },
];

/**
 * Menu groups for sidebar navigation with logical grouping.
 */
export const menuGroups: MenuGroup[] = [
  {
    key: 'identity',
    label: 'Identity & Access',
    icon: 'lucideUsers',
    items: [
      entityConfigList.find((e) => e.key === 'users')!,
      entityConfigList.find((e) => e.key === 'roles')!,
      entityConfigList.find((e) => e.key === 'api-keys')!,
    ],
  },
  {
    key: 'merchants',
    label: 'Merchants',
    icon: 'lucideStore',
    items: [
      entityConfigList.find((e) => e.key === 'merchants')!,
      entityConfigList.find((e) => e.key === 'merchant-applications')!,
      entityConfigList.find((e) => e.key === 'wallets')!,
      entityConfigList.find((e) => e.key === 'wallet-statements')!,
    ],
  },
  {
    key: 'configuration',
    label: 'Configuration',
    icon: 'lucideSlidersHorizontal',
    items: [
      entityConfigList.find((e) => e.key === 'entities')!,
      entityConfigList.find((e) => e.key === 'providers')!,
    ],
  },
  {
    key: 'finances',
    label: 'Finances',
    icon: 'lucideBanknote',
    items: [
      entityConfigList.find((e) => e.key === 'journal-entries')!,
      entityConfigList.find((e) => e.key === 'accounts')!,
      entityConfigList.find((e) => e.key === 'currencies')!,
      entityConfigList.find((e) => e.key === 'countries')!,
      entityConfigList.find((e) => e.key === 'exchange-rates')!,
      entityConfigList.find((e) => e.key === 'fee-analytics')!,
      entityConfigList.find((e) => e.key === 'financial-entity-balances')!,
      entityConfigList.find((e) => e.key === 'provider-balances')!,
      entityConfigList.find((e) => e.key === 'merchant-funding-topups')!,
      entityConfigList.find((e) => e.key === 'market-currencies')!,
      entityConfigList.find((e) => e.key === 'pricing-rules')!,
      entityConfigList.find((e) => e.key === 'transaction-amount-limits')!,
    ],
  },
  {
    key: 'transactions',
    label: 'Transactions',
    icon: 'lucideArrowLeftRight',
    items: [
      entityConfigList.find((e) => e.key === 'deposits')!,
      entityConfigList.find((e) => e.key === 'payouts')!,
      entityConfigList.find((e) => e.key === 'direct-debits')!,
      entityConfigList.find((e) => e.key === 'refunds')!,
      entityConfigList.find((e) => e.key === 'remittances')!,
      entityConfigList.find((e) => e.key === 'batch-disbursements')!,
      entityConfigList.find((e) => e.key === 'settlements')!,
    ],
  },
  {
    key: 'provider-bank',
    label: 'Provider Bank',
    icon: 'lucideLandmark',
    items: [
      entityConfigList.find((e) => e.key === 'provider-bank-accounts')!,
      entityConfigList.find((e) => e.key === 'bank-statements')!,
      entityConfigList.find((e) => e.key === 'account-transfers')!,
      entityConfigList.find((e) => e.key === 'bank-pulls')!,
      entityConfigList.find((e) => e.key === 'bank-pushes')!,
    ],
  },
  {
    key: 'messaging',
    label: 'Messaging',
    icon: 'lucideMessageSquare',
    items: [
      entityConfigList.find((e) => e.key === 'message-queues')!,
      entityConfigList.find((e) => e.key === 'campaigns')!,
      entityConfigList.find((e) => e.key === 'campaign-templates')!,
      entityConfigList.find((e) => e.key === 'campaign-categories')!,
      entityConfigList.find((e) => e.key === 'mailings')!,
    ],
  },
  {
    key: 'system',
    label: 'System',
    icon: 'lucideSettings',
    items: [
      entityConfigList.find((e) => e.key === 'notifications')!,
      entityConfigList.find((e) => e.key === 'webhook-events')!,
      entityConfigList.find((e) => e.key === 'webhook-rules')!,
      entityConfigList.find((e) => e.key === 'audit-logs')!,
      entityConfigList.find((e) => e.key === 'provider-api-logs')!,
      entityConfigList.find((e) => e.key === 'merchant-api-call-logs')!,
    ],
  },
];

export function getEntityConfig(key: string): EntityConfig | undefined {
  return entityConfigList.find((c) => c.key === key);
}

/**
 * Whether the user may open list/detail/create/edit for this entity key.
 */
export function canAccessEntityKey(entityKey: string, effectiveRole: RoleCodeValue | null): boolean {
  const cfg = getEntityConfig(entityKey);
  if (!cfg) {
    return false;
  }
  return canAccessWithAllowedRoles(cfg.allowedRoles, effectiveRole);
}

/**
 * Sidebar menu groups with items the effective role may access; empty groups removed.
 */
export function filterMenuGroupsByEffectiveRole(
  groups: MenuGroup[],
  effectiveRole: RoleCodeValue | null,
): MenuGroup[] {
  if (!effectiveRole) {
    return [];
  }
  return groups
    .map((g) => ({
      ...g,
      items: g.items.filter((item) => canAccessWithAllowedRoles(item.allowedRoles, effectiveRole)),
    }))
    .filter((g) => g.items.length > 0);
}

export { RoleCode, type RoleCodeValue } from '../utils/role-access';
